import { Component, Input, OnDestroy, OnInit } from "@angular/core"
import { ActivatedRoute, NavigationExtras, Params, Router } from "@angular/router"
import { Subscription } from "rxjs"
import { LearningObject } from "../../../models/learningObject.model"
import { BrowsingService } from "../../../services/browsing.service"
import { GTMService } from "../../../services/gtm.service"
import { SearchingService } from "../../../services/searching.service"
import { AddOnViewService } from "../../../services/add-on-view.service"
import Utils from "../../../utils/utils"
import { routeAnimations, routeTransition } from "../../../utils/animations"

@Component({
    selector: "app-search-results-group",
    templateUrl: "./search-results-group.component.html",
    styleUrls: ["./search-results-group.component.scss"],
    standalone: false,
})
export class SearchResultsGroupComponent implements OnInit, OnDestroy {
    subscriptions: Subscription[] = []
    customSubscriptions: Subscription[] = []
    learningObjectResults: LearningObject[] = []
    nextUri: string = ""
    firstNextUri: string = ""
    resultsCount: number = 0
    bootstrapClasses: string = ""
    didYouMeanSuggestions: any = { highlighted: "", text: "" }
    gtmPrefix: string = "gtm-results-search-card"
    currentPage: number = 1
    loadingResults: boolean = true
    resultsPerPage: number
    totalResultsForPagination: number = 0
    resultsPlaceholder = []
    isStudent: boolean = false
    @Input() public resultsPerRow: number = 1

    constructor(
        public browsingService: BrowsingService,
        public searchingService: SearchingService,
        public route: ActivatedRoute,
        public router: Router,
        public gtmService: GTMService,
        public addOnViewService: AddOnViewService
    ) {
        this.resultsPerPage = this.searchingService.resultsPerPage
        this.resultsPlaceholder = new Array(this.resultsPerPage)
    }

    public ngOnInit() {
        this.bootstrapClasses = `col-xs-12 col-lg-${Math.floor(12 / this.resultsPerRow)}`
        this.isStudent = Utils.hasStudentExperience()
        this.subscriptions.push(
            this.route.queryParams.subscribe((queryParams: Params) => {
                this.gtmService.resetDataLayer()
                this.gtmService.pushPageView()

                this.getSearchResults(queryParams)

                if (queryParams.page) {
                    this.currentPage = parseInt(queryParams.page, 10)
                }
            }),

            this.searchingService.resultsAreLoading.subscribe(
                (loading: boolean) => (this.loadingResults = loading)
            )
        )
    }

    public ngOnDestroy() {
        this.subscriptions.map((subscription) => subscription.unsubscribe())
        this.customSubscriptions.map((subscription) => subscription.unsubscribe())
    }

    public onDidYouMeanClick() {
        const queryParams = { q: this.didYouMeanSuggestions.text }
        this.router.navigate(["search"], {
            queryParams,
            queryParamsHandling: "merge",
        })
    }

    public getSearchResults(queryParams) {
        this.customSubscriptions.map((subscription) => subscription.unsubscribe())
        this.learningObjectResults = []
        this.searchingService.resultsAreLoading.next(true)
        this.customSubscriptions.push(
            this.searchingService.getResults(queryParams).subscribe(
                (data: any) => {
                    this.learningObjectResults = data.objects.map((obj) => new LearningObject(obj))
                    this.nextUri = data.meta.next_uri
                    this.firstNextUri = data.meta.next_uri
                    this.resultsCount = data.meta.total

                    if (this.resultsCount > 10000) {
                        this.totalResultsForPagination = 10000 - this.resultsPerPage
                    } else {
                        this.totalResultsForPagination = this.resultsCount
                    }
                    this.searchingService.availableFacetsChanged.next(data.meta.facets.fields)
                    this.didYouMeanSuggestions = data.meta.suggestions
                    this.searchingService.resultsAreLoading.next(false)
                },
                (error) => console.error(`[CUSTOM Error]${error.message}`)
            )
        )
    }

    public collapseResults() {
        this.learningObjectResults = this.learningObjectResults.slice().splice(0, 10)
        this.nextUri = this.firstNextUri
    }

    public scrollTo(el) {
        el.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
        })
    }

    public goToPage(nextPage: number): void {
        if (this.currentPage !== nextPage) {
            const extras: NavigationExtras = {
                queryParams: { page: nextPage },
                queryParamsHandling: "merge",
                relativeTo: this.route,
            }
            this.router.navigate(["."], extras)
            this.currentPage = nextPage
        }
    }

    public onNext(): void {
        this.goToPage(this.currentPage + 1)
    }

    public onPrev(): void {
        this.goToPage(this.currentPage - 1)
    }

    public onFirst(): void {
        this.goToPage(1)
    }

    public onLast(): void {
        this.goToPage(Math.ceil(this.totalResultsForPagination / this.resultsPerPage))
    }
}
